import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { X } from 'lucide-react';
import { Project, projectAPI } from '@/config';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (project: Project) => void;
  project?: Project | null; // For editing existing project
  mode: 'create' | 'edit';
}

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

export const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  project,
  mode
}) => {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or project changes
  useEffect(() => {
    if (isOpen) {
      setName(project?.name || '');
      setError(null);
    } else {
      setName('');
      setError(null);
    }
  }, [isOpen, project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let result: Project;
      
      if (mode === 'create') {
        result = await projectAPI.create({ name: name.trim() });
      } else if (mode === 'edit' && project) {
        result = await projectAPI.update(project.id, { name: name.trim() });
      } else {
        throw new Error('Invalid operation');
      }

      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg p-6 max-w-md w-full"
        style={{ background: PALETTE.brightSnow }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ color: PALETTE.gunmetal }}>
            {mode === 'create' ? 'Create New Project' : 'Edit Project'}
          </h2>
          <Button
            onClick={handleClose}
            variant="ghost"
            size="sm"
            className="p-1"
            style={{ color: PALETTE.gunmetal }}
            disabled={isLoading}
          >
            <X size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label 
              htmlFor="project-name" 
              className="block text-sm font-medium mb-2"
              style={{ color: PALETTE.gunmetal }}
            >
              Project Name
            </label>
            <Input
              id="project-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter project name..."
              style={{ 
                borderColor: PALETTE.americanSilver,
                color: PALETTE.gunmetal
              }}
              disabled={isLoading}
              autoFocus
            />
          </div>

          {error && (
            <div 
              className="text-sm p-3 rounded"
              style={{ 
                background: '#fef2f2', 
                color: '#dc2626',
                border: '1px solid #fecaca'
              }}
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="flex-1"
              style={{ 
                borderColor: PALETTE.americanSilver, 
                color: PALETTE.gunmetal 
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              style={{ 
                background: PALETTE.inchworm, 
                color: PALETTE.gunmetal,
                border: 'none'
              }}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal; 